import { Switch as ChakraSwitch, Span, Text } from '@chakra-ui/react';
import * as React from 'react';
import { labelStyles } from './label';
import { HiX, HiCheck } from 'react-icons/hi';
import { InfoTip, Alert } from '.';

export interface SwitchProps {
  value?: boolean | null | undefined; // this is external state managed value
  defaultValue?: boolean;
  onValueChange: (value: boolean) => void;
  label?: string;
  infoTip?: string;
  minWidth?: number;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  rootRef?: React.RefObject<HTMLLabelElement | null>;
  trackLabel?: { on: React.ReactNode; off: React.ReactNode };
  thumbLabel?: { on: React.ReactNode; off: React.ReactNode };
  disabled?: boolean;
  triggerResetToDefault?: number;
  isBorder?: boolean;
  colorPalette?: string;
  [key: string]: any;
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  function Switch(props, ref) {
    const {
      value,
      inputProps,
      children,
      rootRef,
      minWidth,
      trackLabel,
      thumbLabel,
      defaultValue,
      onValueChange,
      disabled,
      label,
      infoTip,
      triggerResetToDefault,
      isBorder = true,
      colorPalette,
      ...rest
    } = props;

    const [checked, setChecked] = React.useState(defaultValue ?? false);
    const triggerResetToDefaultRef = React.useRef(triggerResetToDefault);

    React.useEffect(() => {
      if (triggerResetToDefaultRef.current === triggerResetToDefault - 1) {
        !value && setChecked(defaultValue);
        onValueChange(defaultValue ?? false);
        triggerResetToDefaultRef.current = triggerResetToDefault;
      }
    }, [triggerResetToDefault]);

    React.useEffect(() => {
      if (value === null || value === undefined) {
        setChecked(defaultValue ?? false);
        onValueChange(defaultValue ?? false);
      }
    }, [value]);

    return (
      <ChakraSwitch.Root
        ref={rootRef}
        checked={value !== null && value !== undefined ? value : checked}
        onCheckedChange={(e) => {
          if (value === null || value === undefined) {
            setChecked(e.checked);
          }
          onValueChange(e.checked);
        }}
        {...(disabled && { invalid: true })}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        transition="all 0.2s"
        width="100%"
        minWidth={
          label
            ? `${Math.max(minWidth || 0, label.length * 6 + 40)}px`
            : 'fit-content'
        }
        maxWidth={'fit-content'}
        disabled={disabled}
        {...(labelStyles.floating.border({
          colorPalette,
          isBorder: isBorder,
        }) as any)}
        colorPalette={colorPalette ?? 'gray'}
        gap={2}
        {...rest}
      >
        {label && (
          <Text
            color={colorPalette}
            {...(labelStyles.floating({
              colorPalette,
              isBorder: isBorder,
            }) as any)}
          >
            {label}
            {infoTip && (
              <Span pointerEvents="auto">
                <InfoTip>
                  <Alert>{' ' + infoTip}</Alert>
                </InfoTip>
              </Span>
            )}
          </Text>
        )}

        <ChakraSwitch.HiddenInput ref={ref} {...inputProps} />
        {children != null && (
          <ChakraSwitch.Label {...labelStyles({ colorPalette })}>
            {children}
          </ChakraSwitch.Label>
        )}
        <ChakraSwitch.Control>
          <ChakraSwitch.Thumb>
            {thumbLabel && (
              <ChakraSwitch.ThumbIndicator fallback={thumbLabel.off}>
                {thumbLabel?.on || <HiCheck color="black" />}
              </ChakraSwitch.ThumbIndicator>
            )}
          </ChakraSwitch.Thumb>
          {trackLabel && (
            <ChakraSwitch.Indicator fallback={trackLabel.off}>
              {trackLabel.on}
            </ChakraSwitch.Indicator>
          )}
        </ChakraSwitch.Control>
      </ChakraSwitch.Root>
    );
  },
);

export interface SwitchOnOffProps extends Omit<
  SwitchProps,
  'onValueChange' | 'value'
> {}

export const SwitchOnOff = ({
  value = undefined,
  onValueChange,
  triggerResetToDefault = 0,
  defaultValue = false,
  label,
  disabled,
  trackLabel,
  thumbLabel,
  colorPalette,
  ...rest
}: SwitchOnOffProps) => {
  return (
    <Switch
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      size="md"
      label={label}
      disabled={disabled}
      triggerResetToDefault={triggerResetToDefault}
      // trackLabel={{
      //   on: <span style={{ color: 'green' }}></span>,
      //   off: <span style={{ color: 'red' }}></span>,
      // }}
      trackLabel={
        trackLabel || {
          on: (
            <Text fontSize={'2xs'} color="green">
              On
            </Text>
          ),
          off: (
            <Text fontSize={'2xs'} color="red">
              Off
            </Text>
          ),
        }
      }
      thumbLabel={
        thumbLabel || {
          on: <HiCheck color="green" size={'0.6rem'} />,
          off: <HiX color="red" size={'0.6rem'} />,
        }
      }
      colorPalette={colorPalette}
      {...rest}
    />
  );
};

export interface SwitchtoEnableButtonProps extends Omit<
  SwitchProps,
  'onValueChange' | 'value'
> {
  title: string;
  enableEdit: boolean;
  setEnableEdit: (enableEdit: boolean) => void;
  handleSave?: () => void;
  buttonLabel?: string;
}

// export const SwitchtoEnableButton = ({
//   title,
//   enableEdit,
//   setEnableEdit,
//   handleSave,
//   buttonLabel,
//   infoTip,
//   label,
//   disabled,
//   trackLabel,
//   thumbLabel,
//   triggerResetToDefault,
//   ...rest
// }: SwitchtoEnableButtonProps) => (
//   <HStack alignSelf={'flex-end'} gap={8}>
//     <Switch
//       colorPalette="gray"
//       value={enableEdit}
//       defaultValue={enableEdit}
//       onValueChange={setEnableEdit}
//       color="secondary"
//       size="lg"
//       label={title}
//       infoTip={infoTip || 'Enable edit mode to update the data'}
//       disabled={disabled}
//       triggerResetToDefault={triggerResetToDefault}
//       trackLabel={{
//         on: <span style={{ color: 'green', fontSize: '0.6rem' }}>On</span>,
//         off: <span style={{ color: 'red', fontSize: '0.6rem' }}>Off</span>,
//       }}
//       thumbLabel={
//         thumbLabel || {
//           on: <HiCheck color="green" />,
//           off: <HiX color="red" />,
//         }
//       }
//       {...rest}
//     />

//     {handleSave && (
//       <Button disabled={!enableEdit} onClick={handleSave} color={'tertiary.fg'}>
//         {buttonLabel || 'Update Permissions'}
//       </Button>
//     )}
//   </HStack>
// );
